import React, { useState, useEffect, useCallback } from "react";
import { usePaginated, useAsync } from "../hooks";
import { api } from "../api";
import {
  useToast, Btn, Card, Spinner, ApiError, Modal,
  Pages, Inp, Sel, Txa, Empty, Badge,
} from "../ui";

// ─── ROLE CONFIG ──────────────────────────────────────────────────────────────
const ROLES = ["owner", "admin", "agent", "support", "viewer"];
const ROLE_COLORS = {
  owner:   "bg-amber-100 text-amber-800",
  admin:   "bg-violet-100 text-violet-800",
  agent:   "bg-blue-100 text-blue-700",
  support: "bg-sky-100 text-sky-700",
  viewer:  "bg-slate-100 text-slate-600",
};
const ROLE_DESCRIPTIONS = {
  owner:   "Full access, can manage team settings",
  admin:   "Can manage members and all CRM records",
  agent:   "Can create and manage own clients & tasks",
  support: "Can view and update assigned records",
  viewer:  "Read-only access",
};

const STATUS_COLORS = {
  active:   "bg-emerald-100 text-emerald-700",
  invited:  "bg-amber-100 text-amber-700",
  inactive: "bg-slate-100 text-slate-500",
};

const TEAM_COLORS = [
  "var(--brand)","#3b82f6","#8b5cf6","#10b981",
  "#f43f5e","#f97316","#06b6d4","#84cc16",
];

// ─── SMALL HELPERS ────────────────────────────────────────────────────────────
const Avatar = ({ name, color = "var(--brand)", size = "md", src }) => {
  const s = { sm: "w-7 h-7 text-xs", md: "w-10 h-10 text-sm", lg: "w-14 h-14 text-xl" }[size];
  const initials = (name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  if (src) return <img src={src} alt={name} className={`${s} rounded-xl object-cover flex-shrink-0`}/>;
  return (
    <div className={`${s} rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0`}
      style={{ background: color }}>
      {initials}
    </div>
  );
};

// ─── TEAM FORM ────────────────────────────────────────────────────────────────
function TeamForm({ initial = {}, onSubmit, onClose, saving }) {
  const [f, sf] = useState({
    name:        initial.name        || "",
    description: initial.description || "",
    color:       initial.color       || "var(--brand)",
    isActive:    initial.isActive    !== false,
  });
  const set = k => e => sf(p => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(f); }} className="space-y-5">
      <Inp label="Team Name *" placeholder="Lagos Sales Team" value={f.name} onChange={set("name")} required/>
      <Txa label="Description" placeholder="What does this team handle?" value={f.description} onChange={set("description")} rows={3}/>

      {/* Colour picker */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Team Colour</label>
        <div className="flex gap-2 flex-wrap">
          {TEAM_COLORS.map(c => (
            <button key={c} type="button" onClick={() => sf(p => ({ ...p, color: c }))}
              className={`w-8 h-8 rounded-lg transition-all ${f.color === c ? "ring-2 ring-offset-2 ring-slate-400 scale-110" : "hover:scale-105"}`}
              style={{ background: c }}/>
          ))}
          <div className="flex items-center gap-2 ml-1">
            <input type="color" value={f.color} onChange={set("color")}
              className="w-8 h-8 rounded-lg border border-slate-200 cursor-pointer p-0.5"/>
            <span className="text-xs text-slate-400">{f.color}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input type="checkbox" id="active" checked={f.isActive} onChange={set("isActive")} className="w-4 h-4 accent-[rgb(var(--brand-rgb))]"/>
        <label htmlFor="active" className="text-sm text-slate-600 font-medium">Active Team</label>
      </div>

      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <Btn type="submit" variant="primary" className="flex-1" loading={saving}>Save Team</Btn>
        <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </form>
  );
}

// ─── ADD MEMBER FORM ─────────────────────────────────────────────────────────
function AddMemberForm({ onSubmit, onClose, saving }) {
  const [f, sf] = useState({
    userId: "", fullName: "", email: "", phone: "",
    jobTitle: "", role: "agent",
  });
  const set = k => e => sf(p => ({ ...p, [k]: e.target.value }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(f); }} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Inp label="Full Name *"  placeholder="Amara Okafor"            value={f.fullName} onChange={set("fullName")} required className="col-span-2"/>
        <Inp label="Email *"      type="email" placeholder="amara@agency.com" value={f.email}    onChange={set("email")}    required/>
        <Inp label="Phone"        placeholder="+234..."                  value={f.phone}    onChange={set("phone")}/>
        <Inp label="User ID *"    placeholder="Your auth system user ID" value={f.userId}   onChange={set("userId")}   required/>
        <Inp label="Job Title"    placeholder="Senior Travel Agent"      value={f.jobTitle} onChange={set("jobTitle")}/>
      </div>

      {/* Role picker */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Role *</label>
        <div className="grid grid-cols-1 gap-2">
          {ROLES.map(r => (
            <label key={r} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${f.role === r ? "border-[rgb(var(--brand-rgb))] bg-[rgb(var(--brand-rgb)/0.05)]" : "border-slate-100 hover:border-slate-200"}`}>
              <input type="radio" name="role" value={r} checked={f.role === r} onChange={set("role")} className="accent-[rgb(var(--brand-rgb))]"/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${ROLE_COLORS[r]}`}>{r}</span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{ROLE_DESCRIPTIONS[r]}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <Btn type="submit" variant="primary" className="flex-1" loading={saving}>Add Member</Btn>
        <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </form>
  );
}

// ─── EDIT MEMBER FORM ────────────────────────────────────────────────────────
function EditMemberForm({ member, onSubmit, onClose, saving }) {
  const [f, sf] = useState({
    fullName: member.fullName  || "",
    phone:    member.phone     || "",
    jobTitle: member.jobTitle  || "",
    role:     member.role      || "agent",
    status:   member.status    || "active",
  });
  const set = k => e => sf(p => ({ ...p, [k]: e.target.value }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(f); }} className="space-y-4">
      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl mb-2">
        <Avatar name={member.fullName} color="#9B32F0" size="md"/>
        <div>
          <p className="font-semibold text-slate-800 text-sm">{member.fullName}</p>
          <p className="text-xs text-slate-400">{member.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Inp label="Full Name" value={f.fullName} onChange={set("fullName")} className="col-span-2"/>
        <Inp label="Phone"     placeholder="+234..." value={f.phone}    onChange={set("phone")}/>
        <Inp label="Job Title" placeholder="Senior Travel Agent" value={f.jobTitle} onChange={set("jobTitle")}/>
      </div>

      <Sel label="Role" value={f.role} onChange={set("role")} options={ROLES.map(r => ({ value: r, label: r.charAt(0).toUpperCase() + r.slice(1) }))}/>
      <Sel label="Status" value={f.status} onChange={set("status")} options={[
        { value: "active",   label: "Active"   },
        { value: "invited",  label: "Invited"  },
        { value: "inactive", label: "Inactive" },
      ]}/>

      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <Btn type="submit" variant="primary" className="flex-1" loading={saving}>Save Changes</Btn>
        <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </form>
  );
}

// ─── TEAM DETAIL SLIDE-OVER ───────────────────────────────────────────────────
function TeamDetailPanel({ teamId, onClose, onMemberUpdated }) {
  const toast = useToast();
  const [team, setTeam]       = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null); // addMember | editMember
  const [selMember, setSelMember] = useState(null);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/crm/teams/${teamId}`);
      setTeam(res.team);
      setMembers(res.members || []);
    } catch(e) { toast(e.message, "error"); }
    finally    { setLoading(false); }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const doAddMember = async form => {
    setSaving(true);
    try { await api.post(`/crm/teams/${teamId}/members`, form); toast("Member added"); setModal(null); load(); onMemberUpdated?.(); }
    catch(e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const doEditMember = async form => {
    setSaving(true);
    try { await api.patch(`/crm/teams/${teamId}/members/${selMember._id}`, form); toast("Member updated"); setModal(null); load(); onMemberUpdated?.(); }
    catch(e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const doRemoveMember = async memberId => {
    // if (!confirm("Remove this member from the team?")) return;
    try { await api.delete(`/crm/teams/${teamId}/members/${memberId}`); toast("Member removed"); load(); onMemberUpdated?.(); }
    catch(e) { toast(e.message, "error"); }
  };

  const doQuickRole = async (member, role) => {
    try { await api.patch(`/crm/teams/${teamId}/members/${member._id}`, { role }); toast(`Role updated to ${role}`); load(); onMemberUpdated?.(); }
    catch(e) { toast(e.message, "error"); }
  };

  const roleOrder = { owner: 0, admin: 1, agent: 2, support: 3, viewer: 4 };
  const sorted = [...members].sort((a, b) => (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9));

  const roleCounts = members.reduce((acc, m) => { acc[m.role] = (acc[m.role] || 0) + 1; return acc; }, {});

  if (loading) return <div className="p-8"><Spinner/></div>;
  if (!team)   return null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex-shrink-0">
        <div className="flex items-start gap-4 mb-4">
          <Avatar name={team.name} color={team.color} size="lg"/>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl font-bold text-slate-800">{team.name}</h2>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${team.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {team.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            {team.description && <p className="text-sm text-slate-400">{team.description}</p>}
          </div>
          <Btn variant="primary" size="sm" onClick={() => setModal("addMember")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v16m8-8H4"/></svg>
            Add Member
          </Btn>
        </div>

        {/* Role breakdown pills */}
        <div className="flex gap-2 flex-wrap">
          {Object.entries(roleCounts).map(([role, count]) => (
            <span key={role} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${ROLE_COLORS[role]}`}>
              {role} <span className="opacity-60">·</span> {count}
            </span>
          ))}
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            {members.length} total
          </span>
        </div>
      </div>

      {/* Member list */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-2">
        {!sorted.length ? (
          <div className="text-center py-14 text-slate-400 text-sm">
            <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-3 mx-auto text-slate-300">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            </div>
            No members yet — add the first one
          </div>
        ) : sorted.map(m => (
          <div key={m._id} className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors group">
            <Avatar name={m.fullName} src={m.avatarUrl} color={team.color} size="md"/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <p className="text-sm font-semibold text-slate-800">{m.fullName}</p>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${ROLE_COLORS[m.role]}`}>{m.role}</span>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${STATUS_COLORS[m.status]}`}>{m.status}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
                <span>{m.email}</span>
                {m.jobTitle && <><span>·</span><span>{m.jobTitle}</span></>}
                {m.phone    && <><span>·</span><span>{m.phone}</span></>}
              </div>
            </div>

            {/* Quick role change dropdown */}
            <select
              value={m.role}
              onChange={e => doQuickRole(m, e.target.value)}
              className="text-xs px-2 py-1.5 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] bg-white capitalize hidden group-hover:block"
              onClick={e => e.stopPropagation()}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>

            <div className="flex gap-1">
              <Btn variant="ghost" size="sm" onClick={() => { setSelMember(m); setModal("editMember"); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
              </Btn>
              <Btn variant="danger" size="sm" onClick={() => doRemoveMember(m._id)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
              </Btn>
            </div>
          </div>
        ))}
      </div>

      {modal === "addMember" && (
        <Modal title="Add Team Member" onClose={() => setModal(null)}>
          <AddMemberForm onSubmit={doAddMember} onClose={() => setModal(null)} saving={saving}/>
        </Modal>
      )}
      {modal === "editMember" && selMember && (
        <Modal title="Edit Member" onClose={() => setModal(null)}>
          <EditMemberForm member={selMember} onSubmit={doEditMember} onClose={() => setModal(null)} saving={saving}/>
        </Modal>
      )}
    </div>
  );
}

// ─── TEAMS TAB ────────────────────────────────────────────────────────────────
export function TeamsTab() {
  const toast = useToast();
  const { data: teams, total, page, totalPages, loading, error, setPage, refetch } = usePaginated("/crm/teams");
  const { data: summary, refetch: refetchSummary } = useAsync(() => api.get("/crm/teams/summary"));
  const [modal, setModal]   = useState(null); // create | edit | detail
  const [sel, setSel]       = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const shown = (teams || []).filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase())
  );

  const doCreate = async form => {
    setSaving(true);
    try { await api.post("/crm/teams", form); toast("Team created"); setModal(null); refetch(); refetchSummary(); }
    catch(e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const doEdit = async form => {
    setSaving(true);
    try { await api.patch(`/crm/teams/${sel._id}`, form); toast("Team updated"); setModal(null); refetch(); refetchSummary(); }
    catch(e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const doDelete = async id => {
    // if (!confirm("Delete this team and remove all its members?")) return;
    try { await api.delete(`/crm/teams/${id}`); toast("Team deleted"); refetch(); refetchSummary(); if (modal === "detail") setModal(null); }
    catch(e) { toast(e.message, "error"); }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Teams</h1>
        <Btn variant="primary" onClick={() => setModal("create")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v16m8-8H4"/></svg>
          New Team
        </Btn>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[rgb(var(--brand-rgb))] flex items-center justify-center flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8"><path d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064"/></svg>
            </div>
            <div>
              <p className="text-xl font-bold text-slate-800 leading-none">{summary.totalTeams}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mt-0.5">Teams</p>
            </div>
          </Card>
          <Card className="p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            </div>
            <div>
              <p className="text-xl font-bold text-slate-800 leading-none">{summary.totalMembers}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mt-0.5">Members</p>
            </div>
          </Card>
          {(summary.byRole || []).slice(0, 2).map(r => (
            <Card key={r.role} className="p-4 flex items-center gap-3">
              <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold capitalize flex-shrink-0 ${ROLE_COLORS[r.role]}`}>
                {r.role.charAt(0).toUpperCase()}
              </span>
              <div>
                <p className="text-xl font-bold text-slate-800 leading-none">{r.count}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mt-0.5 capitalize">{r.role}s</p>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Search */}
      <Card className="p-4">
        <div className="relative">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" className="absolute left-3 top-1/2 -translate-y-1/2">
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search teams…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"/>
        </div>
      </Card>

      {error   && <ApiError msg={error} onRetry={refetch}/>}
      {loading && <Spinner full/>}

      {!loading && !error && (
        <>
          {!shown.length
            ? <Empty icon="clients" title="No teams found" sub={search ? "Try a different search" : "Create your first team"}
                action={!search && <Btn variant="primary" onClick={() => setModal("create")}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v16m8-8H4"/></svg>New Team</Btn>}/>
            : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {shown.map(team => (
                  <Card key={team._id} className="p-5 hover:shadow-md transition-all hover:-translate-y-0.5 duration-200">
                    {/* Team header */}
                    <div className="flex items-start gap-3 mb-4">
                      <Avatar name={team.name} color={team.color} size="md"/>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-slate-800 text-sm leading-tight">{team.name}</p>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${team.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                            {team.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                        {team.description && (
                          <p className="text-xs text-slate-400 mt-0.5 truncate">{team.description}</p>
                        )}
                      </div>
                    </div>

                    {/* Member count + colour accent */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (team.memberCount || 0) * 10)}%`, background: team.color }}/>
                      </div>
                      <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">
                        {team.memberCount || 0} member{team.memberCount !== 1 ? "s" : ""}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Btn variant="secondary" size="sm" className="flex-1"
                        onClick={() => { setSel(team); setModal("detail"); }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                        Members
                      </Btn>
                      <Btn variant="ghost" size="sm" onClick={() => { setSel(team); setModal("edit"); }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                      </Btn>
                      <Btn variant="danger" size="sm" onClick={() => doDelete(team._id)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                      </Btn>
                    </div>
                  </Card>
                ))}
              </div>
          }
          <Pages page={page} totalPages={totalPages} total={total} onPage={setPage} loading={loading}/>
        </>
      )}

      {/* Modals */}
      {modal === "create" && (
        <Modal title="Create Team" onClose={() => setModal(null)}>
          <TeamForm onSubmit={doCreate} onClose={() => setModal(null)} saving={saving}/>
        </Modal>
      )}
      {modal === "edit" && sel && (
        <Modal title="Edit Team" onClose={() => setModal(null)}>
          <TeamForm initial={sel} onSubmit={doEdit} onClose={() => setModal(null)} saving={saving}/>
        </Modal>
      )}

      {/* Detail slide-over */}
      {modal === "detail" && sel && (
        <div className="fixed inset-0 z-50 flex" style={{ background: "rgba(15,23,42,.55)", backdropFilter: "blur(4px)" }}>
          <div className="ml-auto w-full max-w-2xl bg-white h-full flex flex-col shadow-2xl"
            style={{ animation: "slideIn .25s ease" }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Team Members</span>
              <button onClick={() => { setModal(null); refetch(); refetchSummary(); }}
                className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <TeamDetailPanel
                teamId={sel._id}
                onClose={() => { setModal(null); refetch(); }}
                onMemberUpdated={() => refetch()}/>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}